import { Autocomplete, Stack, TextField, Typography, Box, Dialog, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PrintIcon from '@mui/icons-material/Print';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { DarkUpperSection, LoadingSpinner, PageSection, StyledButton } from '../../components';
import { useAlert, useCurrentUser, useDatastore } from '../../hooks';
import { useEffect, useMemo, useState } from 'react';
import { UserDetailsProps } from '../../datastore/userDetails';
import { LocationProps, UnknownLocation, LocationType } from '../../datastore/locations';
import { PrinterProps } from '../../datastore/printers';
import { useNavigate } from 'react-router-dom';
import { useDirtyContext } from '../../hooks/useDirtyProvider';
import { ConfirmationDialog } from '../../components/modals/ConfirmDialog';
import { SUPER_ADMINS, UAT_SUPER_ADMINS } from '../../common/constants';
import { captureError } from '../../common/sentry';
import QmInboundLocationSettingsDialog from './QmInboundLocationSettingsDialog';

export default function Settings() {
  const theme = useTheme();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { isDirty, setIsDirty } = useDirtyContext();
  const [details, setDetails] = useState<UserDetailsProps>({} as UserDetailsProps);
  const [locations, setLocations] = useState<LocationProps[]>([]);
  const [printers, setPrinters] = useState<PrinterProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [navigateDestination, setNavigateDestination] = useState('');
  const [originalLocationName, setOriginalLocationName] = useState<string>('');
  const [originalPrinter, setOriginalPrinter] = useState<PrinterProps | null>();
  const [openDirtyDialog, setOpenDirtyDialog] = useState(false);
  const [isQmSettingsDialogOpen, setIsQmSettingsDialogOpen] = useState(false);
  const [isQmInboundLocationSettingsDialogOpen, setIsQmInboundLocationSettingsDialogOpen] = useState(false);
  const store = useDatastore();
  const alert = useAlert();
  const { isDatastoreReady } = store;
  // Phased load is always on: master-data writes wait until the full sync completes,
  // so we don't save against a partially-synced local store.
  const masterDataWritesReady = isDatastoreReady;

  const canManageQmInboundLocationSettings = details?.qmListRole === 'QM_LIST_EDIT_ALL';

  const adminPanelAccessSettingsAccess = useMemo(() => {
    const superAdminList = process.env.REACT_APP_IS_PROD === 'true' ? SUPER_ADMINS : UAT_SUPER_ADMINS;

    return superAdminList.map((sa) => sa.toLowerCase()).includes(user.email.toLowerCase());
  }, [user.email]);

  useEffect(() => {
    async function getData() {
      try {
        const results = await store.userDetails.get(user.cognitoUser.username, user.cognitoUser);
        if (results) {
          setDetails(results);
          setOriginalLocationName(results.location?.name || '');
          setOriginalPrinter(results.printer ?? null);
          setIsLoading(false);
        }
      } catch (error) {
        captureError(error, 'settings-getData');
        alert('Unable to load your settings', 'error');
      }
    }

    async function getLocationData() {
      try {
        const results = await store.locations.getList();
        const mainLocations = results?.filter((l) => l.type === LocationType.mainLocation && !l.isDisabled);
        if (mainLocations) {
          setLocations(mainLocations);
        }
      } catch (error) {
        captureError(error, 'settings-getLocationData');
        alert('Unable to load locations', 'error');
      }
    }

    getData();
    getLocationData();
  }, [alert, store.locations, store.userDetails, user.cognitoUser]);

  useEffect(() => {
    async function getPrinterData() {
      try {
        const results = await store.printers.getAll();
        const enabledPrinters = results?.filter((p) => !p.isDisabled);
        if (enabledPrinters) {
          setPrinters(enabledPrinters);
        }
      } catch (error) {
        captureError(error, 'settings-getPrinterData');
        alert('Unable to load printers', 'error');
      }
    }
    getPrinterData();
  }, [alert, details.printer, store.printers]);

  function updateLocation(name: string) {
    const location = locations.find((l) => l.name === name);
    const isOriginalLocation = location?.name === originalLocationName;
    setDetails((prevDetails) => ({
      ...prevDetails,
      location,
      printer: originalPrinter
    }));

    setIsDirty(!isOriginalLocation);
  }

  async function updatePrinter(value: string | null) {
    if (value) {
      try {
        const newPrinter = await store.printers.getById(value);
        setDetails((prevState) => ({
          ...prevState,
          printer: newPrinter
        }));

        setIsDirty(newPrinter?.id !== originalPrinter?.id);
      } catch (error) {
        captureError(error, 'settings-updatePrinter');
        alert('Unable to select printer', 'error');
      }
    }
  }

  async function handleSaveDetails() {
    if (details?.location?.id === undefined) {
      alert('Please select a location', 'warning');
      return;
    }

    try {
      const results = await store.userDetails.update(details);
      updateDetails(results);
    } catch (error) {
      captureError(error, 'settings-handleSaveDetails');
      alert('Could not update settings', 'error');
    }
  }

  function updateDetails(results: UserDetailsProps) {
    if (results) {
      setDetails(results);
      setIsDirty(false);
      setOriginalLocationName(results.location?.name || '');
      setOriginalPrinter(results.printer);
      alert('Settings updated.', 'success');
    } else {
      alert('Could not update settings', 'error');
    }
  }

  function handleNavigate(path: string) {
    setNavigateDestination(path);
    if (isDirty) {
      setOpenDirtyDialog(true);
    } else {
      navigate(path);
    }
  }

  function handleNavigateAdmin(destination: string) {
    handleNavigate(`/settings/${destination}`);
  }

  function openQmInboundLocationSettingsDialog() {
    setIsQmSettingsDialogOpen(false);
    setIsQmInboundLocationSettingsDialogOpen(true);
  }

  const orderedLocations = [
    { value: '', name: 'Choose Location', id: crypto.randomUUID() },
    ...locations.sort((a, b) => a.name.localeCompare(b.name))
  ].filter((location) => location.name !== UnknownLocation.Name);

  const orderedPrinters = printers.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={2} sx={{ padding: '1em 0' }}>
          <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
            Settings
          </Typography>
          <Typography variant="body1" color={theme.palette.primary.contrastText}>
            Configure your location and printer preferences
          </Typography>
        </Stack>
      </DarkUpperSection>
      {(user.isAdmin || canManageQmInboundLocationSettings) && (
        <PageSection>
          <Stack spacing={3} sx={{ padding: '1.5em 0', maxWidth: '400px', margin: '0 auto' }}>
            <Box
              sx={{
                background:
                  'linear-gradient(145deg, rgba(248,250,252,0.95) 0%, rgba(241,245,249,0.92) 50%, rgba(248,250,252,0.95) 100%)',
                backdropFilter: 'blur(8px)',
                borderRadius: 3,
                padding: 2.5,
                border: `1px solid ${theme.palette.grey[300]}`,
                boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)'
              }}>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsIcon sx={{ fontSize: 24, color: '#0f172a' }} />
                  <Stack spacing={0}>
                    <Typography variant="h6" fontWeight={700}>
                      Admin Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Manage FTL settings
                    </Typography>
                  </Stack>
                </Stack>
                <LoadingSpinner isLoading={isLoading}>
                  {user.isAdmin ? (
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <StyledButton
                        variant="outlined"
                        startIcon={<LocationOnIcon />}
                        sx={{
                          whiteSpace: 'nowrap',
                          paddingY: 1.5,
                          borderRadius: 2,
                          flex: 1
                        }}
                        onClick={() => handleNavigateAdmin('locations')}>
                        Location Settings
                      </StyledButton>
                      <StyledButton
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        sx={{
                          whiteSpace: 'nowrap',
                          paddingY: 1.5,
                          borderRadius: 2,
                          flex: 1
                        }}
                        onClick={() => handleNavigateAdmin('printers')}>
                        Printer Settings
                      </StyledButton>
                    </Stack>
                  ) : null}
                  {adminPanelAccessSettingsAccess ? (
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2} justifyContent="center">
                        <StyledButton
                          variant="outlined"
                          startIcon={<AdminPanelSettingsIcon />}
                          sx={{
                            whiteSpace: 'nowrap',
                            paddingY: 1.5,
                            borderRadius: 2,
                            flex: 1
                          }}
                          onClick={() => handleNavigateAdmin('adminPanelAccessSettings')}>
                          Access Settings
                        </StyledButton>
                      </Stack>
                    </Stack>
                  ) : null}
                  {canManageQmInboundLocationSettings ? (
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <StyledButton
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        sx={{
                          whiteSpace: 'nowrap',
                          paddingY: 1.5,
                          borderRadius: 2,
                          flex: 1
                        }}
                        onClick={() => setIsQmSettingsDialogOpen(true)}>
                        QM Settings
                      </StyledButton>
                    </Stack>
                  ) : null}
                  {user.isAdmin ? (
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <StyledButton
                        variant="outlined"
                        startIcon={<WarehouseIcon />}
                        sx={{
                          whiteSpace: 'nowrap',
                          paddingY: 1.5,
                          borderRadius: 2,
                          flex: 1
                        }}
                        onClick={() => handleNavigate('/settings/warehouse-emails')}>
                        Warehouse Email Configurations
                      </StyledButton>
                    </Stack>
                  ) : null}
                </LoadingSpinner>
              </Stack>
            </Box>
          </Stack>
        </PageSection>
      )}
      <PageSection>
        <LoadingSpinner isLoading={isLoading}>
          <Stack spacing={4} sx={{ padding: '1.5em 0', maxWidth: '400px', margin: '0 auto' }}>
            {/* Location Setting */}
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h4" fontWeight="bold">
                  Location
                </Typography>
                <Typography color={theme.palette.text.secondary}>Select your current working location</Typography>
              </Stack>
              <Autocomplete
                value={orderedLocations.find((loc) => loc.name === details?.location?.name) || null}
                onChange={(event, newValue) => {
                  updateLocation(newValue?.name || '');
                }}
                options={orderedLocations}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                renderInput={(params) => <TextField {...params} placeholder="Search locations..." variant="outlined" />}
                sx={{ minWidth: '200px' }}
              />
            </Stack>

            {/* Printer Setting */}
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h4" fontWeight="bold">
                  Printer
                </Typography>
                <Typography color={theme.palette.text.secondary}>
                  Choose your default printer for label printing
                </Typography>
              </Stack>
              <Autocomplete
                value={orderedPrinters.find((printer) => printer.id === details?.printer?.id) || null}
                onChange={(event, newValue) => {
                  updatePrinter(newValue?.id || null);
                }}
                options={orderedPrinters}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!details?.location?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={!details?.location?.id ? 'Select a location first' : 'Search printers...'}
                    variant="outlined"
                  />
                )}
                sx={{ minWidth: '200px' }}
              />
              {!details?.location?.id && (
                <Typography variant="body2" color={theme.palette.warning.main}>
                  Please select a location before choosing a printer
                </Typography>
              )}
            </Stack>

            {/* Save Changes Button */}
            <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
              <StyledButton
                variant="contained"
                onClick={handleSaveDetails}
                disabled={!isDirty || !masterDataWritesReady}
                title={!masterDataWritesReady ? 'Available once initial load completes' : undefined}
                sx={{ whiteSpace: 'nowrap', px: 6, py: 1.5 }}>
                Save Changes
              </StyledButton>
            </Stack>
          </Stack>
        </LoadingSpinner>
      </PageSection>

      <Dialog
        open={isQmSettingsDialogOpen}
        onClose={() => setIsQmSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'rgba(148,163,184,0.35)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
            boxShadow: '0 18px 36px rgba(15, 23, 42, 0.16)'
          }
        }}>
        <Stack spacing={1.75} sx={{ p: 2.25 }}>
          <Stack spacing={0.35}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>QM Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage reference data used by QM workflows.
            </Typography>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: 'rgba(148,163,184,0.32)',
              background:
                'linear-gradient(135deg, rgba(241,245,249,0.68) 0%, rgba(255,255,255,0.96) 55%, rgba(236,253,245,0.5) 100%)'
            }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack spacing={0.25} sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
                  Inbound Location List
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add, edit, archive, and reactivate QM inbound locations.
                </Typography>
              </Stack>
              <StyledButton variant="contained" onClick={() => void openQmInboundLocationSettingsDialog()}>
                Open Settings
              </StyledButton>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: 'rgba(148,163,184,0.32)',
              background:
                'linear-gradient(135deg, rgba(241,245,249,0.68) 0%, rgba(255,255,255,0.96) 55%, rgba(239,246,255,0.55) 100%)'
            }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack spacing={0.25} sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
                  Site Inbound Settings
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Maintain main locations and set active Site Inbound QM locations.
                </Typography>
              </Stack>
              <StyledButton variant="contained" onClick={() => handleNavigate('/settings/locations')}>
                Open Locations
              </StyledButton>
            </Stack>
          </Paper>

          <Stack direction="row" justifyContent="flex-end">
            <StyledButton variant="outlined" onClick={() => setIsQmSettingsDialogOpen(false)}>
              Close
            </StyledButton>
          </Stack>
        </Stack>
      </Dialog>

      <QmInboundLocationSettingsDialog
        open={isQmInboundLocationSettingsDialogOpen}
        canManage={canManageQmInboundLocationSettings}
        onClose={() => setIsQmInboundLocationSettingsDialogOpen(false)}
      />

      <ConfirmationDialog
        open={openDirtyDialog}
        onClose={() => setOpenDirtyDialog(false)}
        onConfirm={() => navigate(navigateDestination)}
        title="Unsaved Changes"
        content="You have unsaved changes. Are you sure you want to leave the page?"
      />
    </>
  );
}
